/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import CloseButton from '../components/buttons/CloseButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {clearNotificationDialogBaseStyles} from './ClearNotificationDialogStyles';
import NotificationsClearDialog from './NotificationsClearDialog';

export interface ClearSelectedNotificationsDialogProps {
  show: boolean;
  isCheckAll?: boolean;
  isDesktop?: boolean;
  isPhone?: boolean;
  onAction?: () => void;
  onDismiss?: () => void;
};

const useStyles = makeStyles(() => clearNotificationDialogBaseStyles());

/**
 * Component for rendering the confirmation dialog shown when a user clears
 * one or more selected notifications (or all of them) from the Bulk Action
 * Toolbar in the Notifications Drawer.
 *
 * ```typescript
 *
 * import {ClearSelectedNotificationsDialog} from '@ion/dls/src/notifications'
 *
 * <ClearSelectedNotificationsDialog show={clearAllVerification}
 *   isCheckAll={notificationsCheckAll}
 *   isDesktop={isDesktop}
 *   isPhone={isPhone}
 *   onAction={clearAllSelectedNotifications}
 *   onDismiss={() => setClearAllVerification(false)} />
 *
 * ```
 *
 */
export const ClearSelectedNotificationsDialog = ({
  show,
  isCheckAll = false,
  isDesktop = true,
  isPhone = false,
  onAction,
  onDismiss
}: ClearSelectedNotificationsDialogProps) => {
  const classes = useStyles({isPhone});

  if (show) {
    const title = translate(`notifications:dialog.${isCheckAll ? 'clearAll' : 'clearSelected'}`);
    const message = isDesktop
      ? translate(`notifications:dialog.${isCheckAll ? 'allClearConfirmation' : 'clearConfirmation'}`)
      : translate('notifications:dialog.confirmation');

    return (
      <Dialog open classes={{
        root        : classes.userNotificationRoot,
        scrollPaper : classes.scrollPaper,
        paper       : classes.paper
      }}>
        <DialogTitle className={classes.title}>
          <span className={classes.titleText}>
            {title}
          </span>
          <div className={classes.currentCloseButton}>
            <CloseButton onClick={onDismiss} />
          </div>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <DialogContentText className={classes.contentText}>
            {message}
          </DialogContentText>
          <NotificationsClearDialog />
        </DialogContent>
        <DialogActions className={classes.actions}>
          <TertiaryButton onClick={onAction}>
            {translate(`notifications:dialog.${isCheckAll ? 'yesClearAll' : 'yesClear'}`)}
          </TertiaryButton>
          <TertiaryButton onClick={onDismiss}>
            {translate('notifications:dialog.neverMind')}
          </TertiaryButton>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

export default React.memo(ClearSelectedNotificationsDialog);
